import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { createSurplus, updateSurplus, getSurplus, getSurplusTypes } from '@/lib/api';
import { Loader2, X, Upload, Image, Zap, MapPin, Download, FileSpreadsheet, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import ProPaywall from '@/components/ProPaywall';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SurplusForm = ({ isNew = true }) => {
  const navigate = useNavigate();
  const { surplusId } = useParams();
  const { isUSA } = useLocale();
  const { token } = useAuth();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [surplusTypes, setSurplusTypes] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const imageInputRef = useRef(null);
  const excelInputRef = useRef(null);
  
  // Formulario simplificado con campos esenciales
  const [formData, setFormData] = useState({
    surplus_type: '',
    name: '',
    description: '',
    quantity: '',
    unit: 'metros',
    price: '',
    currency: isUSA ? 'USD' : 'MXN',
    location: '',
    images: [],
    colors: [], // New: available colors
    // Valores por defecto para campos opcionales
    category: 'telas',
    immediate_availability: true,
    hide_price: false
  });

  // Color palette options
  const COLOR_OPTIONS = [
    { name: 'Blanco', value: '#FFFFFF', border: true },
    { name: 'Negro', value: '#000000' },
    { name: 'Gris', value: '#6B7280' },
    { name: 'Beige', value: '#D4B896' },
    { name: 'Crema', value: '#FFFDD0' },
    { name: 'Rojo', value: '#EF4444' },
    { name: 'Azul Marino', value: '#1E3A5F' },
    { name: 'Azul Rey', value: '#1E4C8E' },
    { name: 'Azul Cielo', value: '#87CEEB' },
    { name: 'Verde', value: '#22C55E' },
    { name: 'Verde Olivo', value: '#6B8E23' },
    { name: 'Amarillo', value: '#FCD34D' },
    { name: 'Naranja', value: '#F97316' },
    { name: 'Rosa', value: '#EC4899' },
    { name: 'Morado', value: '#8B5CF6' },
    { name: 'Café', value: '#78350F' },
    { name: 'Vino', value: '#7C2D12' },
    { name: 'Coral', value: '#F87171' },
    { name: 'Turquesa', value: '#14B8A6' },
    { name: 'Dorado', value: '#D4AF37' },
  ];

  const toggleColor = (colorValue) => {
    setFormData(prev => ({
      ...prev,
      colors: prev.colors.includes(colorValue)
        ? prev.colors.filter(c => c !== colorValue)
        : [...prev.colors, colorValue]
    }));
  };

  useEffect(() => {
    fetchData();
  }, [isNew, surplusId]);

  const fetchData = async () => {
    try {
      const typesData = await getSurplusTypes();
      setSurplusTypes(typesData);

      if (!isNew && surplusId) {
        const surplus = await getSurplus(surplusId);
        setFormData({
          surplus_type: surplus.surplus_type || '',
          name: surplus.name || '',
          description: surplus.description || '',
          quantity: surplus.quantity?.toString() || '',
          unit: surplus.unit || 'metros',
          price: surplus.price?.toString() || '',
          currency: surplus.currency || (isUSA ? 'USD' : 'MXN'),
          location: surplus.location || '',
          images: surplus.images || [],
          colors: surplus.colors || [],
          category: surplus.category || 'telas',
          immediate_availability: surplus.immediate_availability !== false,
          hide_price: surplus.hide_price || false
        });
      }
    } catch (error) {
      toast.error(isUSA ? 'Error loading data' : 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.surplus_type || !formData.name || !formData.quantity || !formData.location) {
      toast.error(isUSA ? 'Complete the required fields' : 'Completa los campos requeridos');
      return;
    }

    setSaving(true);

    const submitData = {
      ...formData,
      quantity: parseFloat(formData.quantity),
      price: formData.price ? parseFloat(formData.price) : null
    };

    try {
      if (isNew) {
        await createSurplus(submitData);
        toast.success(isUSA ? 'Surplus published successfully' : 'Excedente publicado correctamente');
      } else {
        await updateSurplus(surplusId, submitData);
        toast.success(isUSA ? 'Surplus updated successfully' : 'Excedente actualizado correctamente');
      }
      navigate('/dashboard/surplus');
    } catch (error) {
      if (error.status === 403) {
        setShowPaywall(true);
      } else {
        toast.error(error.message || (isUSA ? 'Error saving' : 'Error al guardar'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    const token = localStorage.getItem('texhub_token');

    try {
      const formDataUpload = new FormData();
      for (let i = 0; i < files.length; i++) {
        formDataUpload.append('files', files[i]);
      }

      const response = await fetch(`${API_URL}/api/upload/images/multiple`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });

      if (!response.ok) {
        throw new Error(isUSA ? 'Error uploading images' : 'Error al subir imágenes');
      }

      const result = await response.json();
      
      if (result.uploaded && result.uploaded.length > 0) {
        const newImages = result.uploaded.map(img => `${API_URL}${img.url}`);
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, ...newImages]
        }));
        toast.success(isUSA 
          ? `${result.uploaded.length} image(s) uploaded`
          : `${result.uploaded.length} imagen(es) subida(s)`
        );
      }
    } catch (error) {
      toast.error(isUSA ? 'Error uploading images' : 'Error al subir las imágenes');
    } finally {
      setUploadingImages(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/surplus/template/excel`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'plantilla_saldos_texhub.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success(isUSA ? 'Template downloaded' : 'Plantilla descargada');
      }
    } catch (error) {
      toast.error(isUSA ? 'Error downloading template' : 'Error al descargar plantilla');
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingExcel(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/surplus/import/excel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const result = await response.json();
      
      if (response.ok) {
        toast.success(`${result.imported} ${isUSA ? 'surplus imported' : 'saldos importados'}`);
        if (result.errors && result.errors.length > 0) {
          toast.warning(`${result.errors.length} ${isUSA ? 'rows with errors' : 'filas con errores'}`);
        }
        navigate('/dashboard/surplus');
      } else {
        toast.error(result.detail || (isUSA ? 'Import error' : 'Error al importar'));
      }
    } catch (error) {
      toast.error(isUSA ? 'Error importing file' : 'Error al importar archivo');
    } finally {
      setUploadingExcel(false);
      if (excelInputRef.current) excelInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="surplus-form">
      <div className="page-header">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="w-6 h-6 text-yellow-500" />
          <h1 className="page-title">
            {isNew 
              ? (isUSA ? 'Publish surplus' : 'Publicar excedente') 
              : (isUSA ? 'Edit surplus' : 'Editar excedente')}
          </h1>
        </div>
        <p className="page-subtitle">
          {isUSA ? 'Sell your available inventory quickly' : 'Vende tu inventario disponible de forma rápida'}
        </p>
      </div>

      {/* Excel Import Banner - Only show for new surplus */}
      {isNew && (
        <Card className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <FileSpreadsheet className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">
                    {isUSA ? 'Have multiple products?' : '¿Tienes varios productos?'}
                  </p>
                  <p className="text-sm text-slate-600">
                    {isUSA ? 'Import with Excel + images ZIP' : 'Importa con Excel + ZIP de imágenes'}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button 
                  type="button"
                  variant="outline" 
                  size="sm"
                  onClick={handleDownloadTemplate}
                  className="border-amber-300 text-amber-700 hover:bg-amber-100"
                  data-testid="download-surplus-template-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {isUSA ? 'Template' : 'Plantilla'}
                </Button>
                <Button 
                  type="button"
                  size="sm"
                  onClick={() => excelInputRef.current?.click()}
                  disabled={uploadingExcel}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="import-surplus-excel-btn"
                >
                  {uploadingExcel ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {isUSA ? 'Import Excel' : 'Importar Excel'}
                </Button>
                <input
                  ref={excelInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleImportExcel}
                  className="hidden"
                />
              </div>
            </div>
            
            {/* ZIP Images info */}
            <div className="mt-3 pt-3 border-t border-amber-200/50">
              <p className="text-xs text-amber-700">
                <strong>{isUSA ? 'Tip:' : 'Tip:'}</strong> {isUSA 
                  ? 'Add image URLs in the Excel or upload a ZIP with images named like your products (e.g., "gabardina_1.jpg")'
                  : 'Agrega URLs de imágenes en el Excel o sube un ZIP con imágenes nombradas como tus productos (ej: "gabardina_1.jpg")'
                }
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit}>
        <Card className="card-industrial mb-6">
          <CardContent className="pt-6 space-y-5">
            {/* Tipo de excedente */}
            <div className="space-y-2">
              <Label>{isUSA ? 'What type of surplus is it? *' : '¿Qué tipo de excedente es? *'}</Label>
              <Select
                value={formData.surplus_type}
                onValueChange={(v) => setFormData({ ...formData, surplus_type: v })}
                required
              >
                <SelectTrigger data-testid="surplus-type-select">
                  <SelectValue placeholder={isUSA ? 'Select type' : 'Seleccionar tipo'} />
                </SelectTrigger>
                <SelectContent>
                  {surplusTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Nombre */}
            <div className="space-y-2">
              <Label htmlFor="name">{isUSA ? 'Listing title *' : 'Título del anuncio *'}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={isUSA ? 'E.g.: 500 meters of white cotton' : 'Ej: 500 metros de algodón blanco'}
                required
                data-testid="surplus-name-input"
              />
            </div>

            {/* Descripción */}
            <div className="space-y-2">
              <Label htmlFor="description">{isUSA ? 'Description' : 'Descripción'}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={isUSA ? 'Briefly describe your product...' : 'Describe brevemente tu producto...'}
                rows={3}
              />
            </div>

            {/* Cantidad y Unidad */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quantity">{isUSA ? 'Quantity *' : 'Cantidad *'}</Label>
                <Input
                  id="quantity"
                  type="number"
                  step="0.01"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  placeholder="500"
                  required
                  data-testid="surplus-quantity-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit">{isUSA ? 'Unit' : 'Unidad'}</Label>
                <Select
                  value={formData.unit}
                  onValueChange={(v) => setFormData({ ...formData, unit: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="metros">{isUSA ? 'Meters' : 'Metros'}</SelectItem>
                    <SelectItem value="kilos">{isUSA ? 'Kilos' : 'Kilos'}</SelectItem>
                    <SelectItem value="piezas">{isUSA ? 'Pieces' : 'Piezas'}</SelectItem>
                    <SelectItem value="rollos">{isUSA ? 'Rolls' : 'Rollos'}</SelectItem>
                    <SelectItem value="yardas">{isUSA ? 'Yards' : 'Yardas'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Precio y Moneda */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">{isUSA ? 'Price - Optional' : 'Precio - Opcional'}</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder={isUSA ? 'Leave empty for quote' : 'Dejar vacío para cotizar'}
                />
              </div>
              <div className="space-y-2">
                <Label>{isUSA ? 'Currency' : 'Moneda'}</Label>
                <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MXN">🇲🇽 MXN</SelectItem>
                    <SelectItem value="USD">🇺🇸 USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-slate-500 -mt-1">
              {isUSA 
                ? "If you don't set a price, interested buyers will contact you for a quote"
                : 'Si no pones precio, los interesados te contactarán para cotizar'
              }
            </p>

            {/* Ubicación */}
            <div className="space-y-2">
              <Label htmlFor="location" className="flex items-center gap-2">
                <MapPin className="w-4 h-4" style={{ color: '#1E4C8E' }} />
                {isUSA ? 'Location *' : 'Ubicación *'}
              </Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder={isUSA ? 'E.g.: Los Angeles, CA' : 'Ej: Guadalajara, Jalisco'}
                required
                data-testid="surplus-location-input"
              />
            </div>

            {/* Colores disponibles */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                {isUSA ? 'Available Colors (optional)' : 'Colores disponibles (opcional)'}
              </Label>
              <p className="text-xs text-slate-500 -mt-1">
                {isUSA ? 'Select all colors available for this product' : 'Selecciona todos los colores disponibles para este producto'}
              </p>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map((color) => {
                  const isSelected = formData.colors.includes(color.value);
                  return (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => toggleColor(color.value)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        isSelected 
                          ? 'bg-slate-900 text-white ring-2 ring-offset-2 ring-slate-900' 
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                      data-testid={`color-option-${color.name}`}
                    >
                      <span 
                        className={`w-4 h-4 rounded-full flex-shrink-0 ${color.border ? 'border border-slate-300' : ''}`}
                        style={{ backgroundColor: color.value }}
                      />
                      {color.name}
                    </button>
                  );
                })}
              </div>
              {formData.colors.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-emerald-600">
                  <span>{formData.colors.length} {isUSA ? 'colors selected' : 'colores seleccionados'}</span>
                  <button 
                    type="button" 
                    onClick={() => setFormData({...formData, colors: []})}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    ({isUSA ? 'clear' : 'limpiar'})
                  </button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Imágenes */}
        <Card className="card-industrial mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Image className="w-5 h-5" style={{ color: '#1E4C8E' }} />
              {isUSA ? 'Photos (optional)' : 'Fotos (opcional)'}
            </CardTitle>
            <CardDescription className="text-sm">
              {isUSA ? 'Photos help sell faster' : 'Las fotos ayudan a vender más rápido'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div 
              className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-slate-400 transition-colors cursor-pointer"
              onClick={() => imageInputRef.current?.click()}
            >
              <input
                ref={imageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                onChange={handleImageUpload}
                className="hidden"
                data-testid="surplus-image-upload"
              />
              {uploadingImages ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  <p className="text-sm text-slate-500">{isUSA ? 'Uploading...' : 'Subiendo...'}</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <Upload className="w-6 h-6 text-slate-400" />
                  <p className="text-sm text-slate-600">{isUSA ? 'Add photos' : 'Agregar fotos'}</p>
                </div>
              )}
            </div>

            {formData.images.length > 0 && (
              <div className="grid grid-cols-4 gap-2">
                {formData.images.map((img, index) => (
                  <div key={index} className="relative group aspect-square">
                    <img 
                      src={img} 
                      alt={isUSA ? `Image ${index + 1}` : `Imagen ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg border border-slate-200"
                    />
                    <button 
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex gap-3">
          <Button type="button" variant="outline" className="flex-1" onClick={() => navigate(-1)}>
            {isUSA ? 'Cancel' : 'Cancelar'}
          </Button>
          <Button 
            type="submit" 
            className="flex-1 hover:opacity-90"
            style={{ backgroundColor: '#34B772' }}
            disabled={saving}
            data-testid="save-surplus-btn"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isUSA ? 'Publishing...' : 'Publicando...'}
              </>
            ) : (
              <>
                <Zap className="mr-2 h-4 w-4" />
                {isUSA ? 'Publish' : 'Publicar'}
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Pro Paywall Modal */}
      <ProPaywall 
        isOpen={showPaywall} 
        onClose={() => setShowPaywall(false)} 
        feature={isUSA ? 'publish surplus' : 'publicar saldos'}
      />
    </div>
  );
};

export default SurplusForm;
